import { takeLatest, put, call, all } from 'redux-saga/effects';
import { keys } from 'lodash';
import { getSupportConnectionStatusTask } from '../../auth/sagas/authSaga';
import {
    GET_DASHBOARD_DATA_REQUEST,
    GET_UPDATES_REQUEST,
    GET_DEVICES_UPDATES_REQUEST,
    GET_WEAK_PASSWORDS_REQUEST,
    GET_VPN_STATUS_REQUEST,
    GET_UPTIME_REQUEST,
    GET_SERVICES_STATUS_REQUEST,
    GET_FIREWALL_POLICIES_STATUS_REQUEST,
    GET_FIREWALL_SERVICES_STATUS_REQUEST,
    GET_WEB_SECURITY_STATUS_REQUEST,
    GET_STORAGE_USAGE_REQUEST,
    GET_PARTITIONS_STATUS_REQUEST,
    GET_ACTIVE_CALLS_REQUEST,
    GET_CALLS_SUMMARY_REQUEST,
    GET_TOTAL_CALLS_REQUEST
} from '../actions/dashboardActionTypes';
import {
    getDashboardDataSuccess,
    getDashboardDataFailure,
    getUptimeSuccess,
    getUptimeFailure,
    getServicesStatusSuccess,
    getServicesStatusFailure,
    getFirewallPoliciesStatusSuccess,
    getFirewallPoliciesStatusFailure,
    getFirewallServicesStatusSuccess,
    getFirewallServicesStatusFailure,
    getWebSecurityStatusSuccess,
    getWebSecurityStatusFailure,
    getPartitionsStatusSuccess,
    getPartitionsStatusFailure,
    getStorageUsageSuccess,
    getStorageUsageFailure,
    getActiveCallsSuccess,
    getActiveCallsFailure,
    getUpdatesSuccess,
    getUpdatesFailure,
    getDevicesUpdatesSuccess,
    getDevicesUpdatesFailure,
    getWeakPasswordsSuccess,
    getWeakPasswordsFailure,
    getVPNStatusSuccess,
    getVPNStatusFailure,
    getCallsSummarySuccess,
    getCallsSummaryFailure,
    getTotalCallsSuccess,
    getTotalCallsFailure
} from '../actions/dashboardActions';
import {
    getUpdatesStatus,
    getDevicesUpdatesStatus,
    getUptime,
    getServicesStatus,
    getFirewallStatus,
    getHttpsStatus,
    getVPNStatus,
    getPasswordsStatus,
    getPartitionStatus,
    getStorageUsage,
    getActiveCalls,
    getCallsSummary,
    getTotalCalls
} from '../services/dashboardServices';
import { buildPartitionData } from '../helpers/parsePartitionData';
import { buildStorageUsageData } from '../helpers/parseStorageUsageData';
import { buildActiveCallsData } from '../helpers/parseActiveCallsData';
import { buildWeakPasswordsData } from '../helpers/parseWeakPasswordsData';
import { buildVPNStatusData } from '../helpers/parseVPNStatusData';
import { buildServicesStatusData } from '../helpers/parseServicesStatusData';
import { buildFirewallPoliciesStatusData } from '../helpers/parseFirewallPoliciesStatusData';
import { buildFirewallServicesStatusData } from '../helpers/parseFirewallServicesStatus';
import { buildWebSecurityStatusData } from '../helpers/parseWebSecurityStatusData';
import { buildCallSummaryData } from '../helpers/parseCallsSummaryData';

const getDashboardDataTask = function* () {
    try {
        yield all([
            call(getWeakPasswordsTask),
            call(getSupportConnectionStatusTask),
            call(getUptimeTask),
            call(getServicesStatusTask),
            call(getFirewallPoliciesStatusTask),
            call(getFirewallServicesStatusTask),
            call(getWebSecurityStatusTask),
            call(getStorageUsageTask),
            call(getPartitionsStatusTask),
            call(getActiveCallsTask),
            call(getCallsSummaryTask),
            call(getTotalCallsTask)
        ]);

        yield put(getDashboardDataSuccess());
    } catch (error) {
        yield put(getDashboardDataFailure());
    }
};

const getUpdatesStatusTask = function* () {
    try {
        const { data: { updatesVM: { bugfixes } } } = yield call(getUpdatesStatus);

        yield put(getUpdatesSuccess(bugfixes.length));
    } catch (error) {
        yield put(getUpdatesFailure());
    }
};

const getDevicesUpdatesStatusTask = function* () {
    try {
        const { data: { updatesDevices: { devices } } } = yield call(getDevicesUpdatesStatus);;

        yield put(getDevicesUpdatesSuccess(keys(devices).length));
    } catch (error) {
        yield put(getDevicesUpdatesFailure());
    }
};

const getWeakPasswordsTask = function* () {
    try {
        const { data: { result: { passwords: { admin_passwds, user_passwds } } } } = yield call(getPasswordsStatus);
        const weakPasswords = buildWeakPasswordsData(admin_passwds, user_passwds);

        yield put(getWeakPasswordsSuccess(weakPasswords));
    } catch (error) {
        yield put(getWeakPasswordsFailure());
    }
};

const getVPNStatusTask = function* () {
    try {
        const { data: vpnData } = yield call(getVPNStatus);
        const vpnStatus = buildVPNStatusData(vpnData);

        yield put(getVPNStatusSuccess(vpnStatus));
    } catch (error) {
        yield put(getVPNStatusFailure());
    }
};

const getUptimeTask = function* () {
    try {
        const { data: { result: { uptime } } } = yield call(getUptime);
        const uptimeStatus = {
            days: uptime.up_day,
            hours: uptime.up_hour,
            minutes: uptime.up_min,
            now: uptime.now_str
        };

        yield put(getUptimeSuccess(uptimeStatus));
    } catch (error) {
        yield put(getUptimeFailure());
    }
};

const getServicesStatusTask = function* () {
    try {
        const { data: { services } } = yield call(getServicesStatus);
        const servicesStatus = buildServicesStatusData(services);

        yield put(getServicesStatusSuccess(servicesStatus));
    } catch (error) {
        yield put(getServicesStatusFailure());
    }
};

const getFirewallPoliciesStatusTask = function* () {
    try {
        const { data: { result: { fw_statuses: { fw_status_policies } } } } = yield call(getFirewallStatus);
        const firewallPolicies = buildFirewallPoliciesStatusData(fw_status_policies);

        yield put(getFirewallPoliciesStatusSuccess(firewallPolicies));
    } catch (error) {
        yield put(getFirewallPoliciesStatusFailure());
    }
};

const getFirewallServicesStatusTask = function* () {
    try {
        const { data: { result: { fw_statuses: { fw_status_services } } } } = yield call(getFirewallStatus);
        const firewallServices = buildFirewallServicesStatusData(fw_status_services);

        yield put(getFirewallServicesStatusSuccess(firewallServices));
    } catch (error) {
        yield put(getFirewallServicesStatusFailure());
    }
};

const getWebSecurityStatusTask = function* () {
    try {
        const { data: { result: { https_statuses } } } = yield call(getHttpsStatus);
        const webSecurity = buildWebSecurityStatusData(https_statuses);

        yield put(getWebSecurityStatusSuccess(webSecurity));
    } catch (error) {
        yield put(getWebSecurityStatusFailure());
    }
};

const getStorageUsageTask = function* () {
    try {
        const { data: { result: { states: { abs } } } } = yield call(getStorageUsage);
        const storageUsage = buildStorageUsageData(abs);

        yield put(getStorageUsageSuccess(storageUsage));
    } catch (error) {
        yield put(getStorageUsageFailure());
    }
};

const getPartitionsStatusTask = function* () {
    try {
        const { data: { result: { states: { partitions } } } } = yield call(getPartitionStatus);
        const partitionsStatus = buildPartitionData(partitions);

        yield put(getPartitionsStatusSuccess(partitionsStatus));
    } catch (error) {
        yield put(getPartitionsStatusFailure());
    }
};

const getActiveCallsTask = function* () {
    try {
        const { data: { result: { calls } } } = yield call(getActiveCalls);
        const activeCalls = buildActiveCallsData(calls);

        yield put(getActiveCallsSuccess(activeCalls));
    } catch (error) {
        yield put(getActiveCallsFailure());
    }
};

const getCallsSummaryTask = function* () {
    try {
        let today = new Date();
        const pastWeek = today.setDate(today.getDate() - 7);
        const from = new Date(pastWeek).getTime() / 1e3;
        const to = new Date().getTime() / 1e3;
        const periodUnit = 'days'; //hours, months
        const period = 1;
        const { data: { calls: summary } } = yield call(getCallsSummary, from, to, periodUnit, period);
        const callsSummary = buildCallSummaryData(summary);

        yield put(getCallsSummarySuccess(callsSummary));
    } catch (error) {
        yield put(getCallsSummaryFailure());
    }
};

const getTotalCallsTask = function* () {
    try {
        const { data: { total_calls } } = yield call(getTotalCalls);

        yield put(getTotalCallsSuccess(+total_calls));
    } catch (error) {
        yield put(getTotalCallsFailure());
    }
};

export default function* dashboardSaga() {
    yield takeLatest(GET_DASHBOARD_DATA_REQUEST, getDashboardDataTask);
    yield takeLatest(GET_UPDATES_REQUEST, getUpdatesStatusTask);
    yield takeLatest(GET_DEVICES_UPDATES_REQUEST, getDevicesUpdatesStatusTask);
    yield takeLatest(GET_WEAK_PASSWORDS_REQUEST, getWeakPasswordsTask);
    yield takeLatest(GET_VPN_STATUS_REQUEST, getVPNStatusTask);
    yield takeLatest(GET_UPTIME_REQUEST, getUptimeTask);
    yield takeLatest(GET_SERVICES_STATUS_REQUEST, getServicesStatusTask);
    yield takeLatest(GET_FIREWALL_POLICIES_STATUS_REQUEST, getFirewallPoliciesStatusTask);
    yield takeLatest(GET_FIREWALL_SERVICES_STATUS_REQUEST, getFirewallServicesStatusTask);
    yield takeLatest(GET_WEB_SECURITY_STATUS_REQUEST, getWebSecurityStatusTask);
    yield takeLatest(GET_STORAGE_USAGE_REQUEST, getStorageUsageTask);
    yield takeLatest(GET_PARTITIONS_STATUS_REQUEST, getPartitionsStatusTask);
    yield takeLatest(GET_ACTIVE_CALLS_REQUEST, getActiveCallsTask);
    yield takeLatest(GET_CALLS_SUMMARY_REQUEST, getCallsSummaryTask);
    yield takeLatest(GET_TOTAL_CALLS_REQUEST, getTotalCallsTask);
};
